import { AdminApi, CountryEnum, Document, DocumentsApi, LegalEntityTypeEnum } from 'api';
import useApiCall from 'hooks/use-api-call';
import React, { FunctionComponent, useMemo, useState } from 'react';
import Translate from 'ui/atoms/translate';
import { Modal, makeModalForm, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons } from 'ui/molecules/modal';
import { StateValues } from 'react-use-form-state';
import useCountries from 'hooks/use-countries';
import { FileWithDocType } from 'ui/atoms/file-upload';
import FileUploadDocumentContainer from 'src/ui/atoms/file-upload/file-upload-document-container';
import * as Styled from './styled';

export interface EditInvestorCompanyDataFields {
  id: string;
  companyName: string;
  legalEntityType?: LegalEntityTypeEnum;
  street: string;
  zip: string;
  city: string;
  country: CountryEnum;
  commercialRegister: string;
  commercialRegisterNumber: string;
  commercialRegisterStatement: FileWithDocType;
  listOfShareholders: FileWithDocType;
  transparencyRegister: FileWithDocType;
  commercialRegisterStatementId?: string;
  listOfShareholdersId?: string;
  transparencyRegisterId?: string;
}

interface Documents {
  commercialRegisterStatement: Document | null;
  listOfShareholders: Document | null;
  transparencyRegister: Document | null;
}
export interface EditCompanyDataModalProps {
  onHideEditCompanyData: () => void;
  fetchInvestorDetails: () => void;
  showSuccessModal: () => void;
  values: Omit<
    EditInvestorCompanyDataFields,
    'commercialRegisterStatement' | 'listOfShareholders' | 'transparencyRegister'
  > &
    Documents;
}

const EditInvestorCompanyDataForm = makeModalForm<EditInvestorCompanyDataFields>();

const EditCompanyDataModal: FunctionComponent<EditCompanyDataModalProps> = (props) => {
  const { fetchInvestorDetails, onHideEditCompanyData, showSuccessModal = () => {}, values } = props;

  const [investorData, setInvestorData] = useState<EditInvestorCompanyDataFields>({
    ...values,
    commercialRegisterStatement: { file: null, documentType: '' },
    listOfShareholders: { file: null, documentType: '' },
    transparencyRegister: { file: null, documentType: '' },
  });
  const [commercialRegisterStatement, setCommercialRegisterStatement] = useState<Document | null>(
    values.commercialRegisterStatement,
  );
  const [listOfShareholders, setListOfShareholders] = useState<Document | null>(values.listOfShareholders);
  const [transparencyRegister, setTransparencyRegister] = useState<Document | null>(values.transparencyRegister);

  const { makeAuthenticatedApi, withApi } = useApiCall();

  const investorsApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const documentsApi: DocumentsApi = useMemo(() => makeAuthenticatedApi(DocumentsApi), [makeAuthenticatedApi]);

  const editCompanyData = (fieldsValues: StateValues<EditInvestorCompanyDataFields>) => {
    withApi(async () => {
      const patchBody = {
        id: values.id,
        adminLegalPersonUpdateRequest: {
          ...fieldsValues,
        },
      };
      if (fieldsValues.commercialRegisterStatement?.file) {
        const commercialRegisterStatementResponse = await documentsApi.adminDocumentsCreate({
          file: fieldsValues.commercialRegisterStatement?.file,
        });
        patchBody.adminLegalPersonUpdateRequest.commercialRegisterStatementId = commercialRegisterStatementResponse.id;
      } else if (commercialRegisterStatement) {
        patchBody.adminLegalPersonUpdateRequest.commercialRegisterStatementId = commercialRegisterStatement.id;
      }

      if (fieldsValues.listOfShareholders?.file) {
        const listOfShareholdersResponse = await documentsApi.adminDocumentsCreate({
          file: fieldsValues.listOfShareholders?.file,
        });
        patchBody.adminLegalPersonUpdateRequest.listOfShareholdersId = listOfShareholdersResponse.id;
      } else if (listOfShareholders) {
        patchBody.adminLegalPersonUpdateRequest.listOfShareholdersId = listOfShareholders.id;
      }

      if (fieldsValues.transparencyRegister?.file) {
        const transparencyRegisterResponse = await documentsApi.adminDocumentsCreate({
          file: fieldsValues.transparencyRegister?.file,
        });
        patchBody.adminLegalPersonUpdateRequest.transparencyRegisterId = transparencyRegisterResponse.id;
      } else if (transparencyRegister) {
        patchBody.adminLegalPersonUpdateRequest.transparencyRegisterId = transparencyRegister.id;
      }

      await investorsApi.adminInvestorsLegalPersonCreate(patchBody);

      showSuccessModal();
      fetchInvestorDetails();
      onHideEditCompanyData();
    });
  };

  return (
    <>
      <Modal onClose={onHideEditCompanyData}>
        <EditInvestorCompanyDataForm
          initial={{
            companyName: investorData.companyName,
            legalEntityType: investorData.legalEntityType,
            street: investorData.street,
            zip: investorData.zip,
            city: investorData.city,
            country: investorData.country,
            commercialRegister: investorData.commercialRegister,
            commercialRegisterNumber: investorData.commercialRegisterNumber,
          }}
          onChange={(values: StateValues<EditInvestorCompanyDataFields>) => {
            setInvestorData(values);
          }}
          onSubmit={(values: StateValues<EditInvestorCompanyDataFields>) => {
            return editCompanyData(values);
          }}
          i18nKey="dashboardIssuerInvestorDetails.editCompanyDataForm"
        >
          <ModalHeader>
            <Translate name="dashboardIssuerInvestorDetails.editCompanyDataForm.title" />
          </ModalHeader>
          <ModalContent>
            <EditInvestorCompanyDataForm.Group name="companyName" required={!!values.companyName}>
              <EditInvestorCompanyDataForm.Input autoFocus={true} placeholder="-" />
            </EditInvestorCompanyDataForm.Group>
            <EditInvestorCompanyDataForm.Group name="legalEntityType" required={true}>
              <EditInvestorCompanyDataForm.Select
                value={values.legalEntityType}
                options={Object.values(LegalEntityTypeEnum).map((legalType) => ({
                  value: legalType,
                  label: legalType,
                }))}
              ></EditInvestorCompanyDataForm.Select>
            </EditInvestorCompanyDataForm.Group>
            <EditInvestorCompanyDataForm.Group name="street" required={!!values.street}>
              <EditInvestorCompanyDataForm.Input placeholder="-" />
            </EditInvestorCompanyDataForm.Group>
            <Styled.Row>
              <Styled.LeftColumn>
                <EditInvestorCompanyDataForm.Group name="zip" required={!!values.zip}>
                  <EditInvestorCompanyDataForm.Input placeholder="-" />
                </EditInvestorCompanyDataForm.Group>
              </Styled.LeftColumn>
              <Styled.RightColumn>
                <EditInvestorCompanyDataForm.Group name="city" required={!!values.city}>
                  <EditInvestorCompanyDataForm.Input value={investorData.city} placeholder="-" />
                </EditInvestorCompanyDataForm.Group>
              </Styled.RightColumn>
            </Styled.Row>
            <EditInvestorCompanyDataForm.Group name="country" required={!!values.country}>
              <EditInvestorCompanyDataForm.Select
                options={useCountries()}
                value={investorData.country}
                onChange={(country: CountryEnum) => {
                  setInvestorData({ ...investorData, country });
                }}
              ></EditInvestorCompanyDataForm.Select>
            </EditInvestorCompanyDataForm.Group>
            <EditInvestorCompanyDataForm.Group name="commercialRegister" required={!!values.commercialRegister}>
              <EditInvestorCompanyDataForm.Input />
            </EditInvestorCompanyDataForm.Group>
            <EditInvestorCompanyDataForm.Group
              name="commercialRegisterNumber"
              required={!!values.commercialRegisterNumber}
            >
              <EditInvestorCompanyDataForm.Input />
            </EditInvestorCompanyDataForm.Group>
            {commercialRegisterStatement && (
              <EditInvestorCompanyDataForm.Group name="commercialRegisterStatement">
                <FileUploadDocumentContainer
                  uploadedFile={{
                    file: {
                      name: commercialRegisterStatement.name,
                      size: commercialRegisterStatement.sizeInBytes,
                    } as File,
                  }}
                  onRemoveDocument={() => setCommercialRegisterStatement(null)}
                />
              </EditInvestorCompanyDataForm.Group>
            )}
            {!commercialRegisterStatement && (
              <EditInvestorCompanyDataForm.Group name="commercialRegisterStatement">
                <EditInvestorCompanyDataForm.FileUpload />
              </EditInvestorCompanyDataForm.Group>
            )}
            {listOfShareholders && (
              <EditInvestorCompanyDataForm.Group name="listOfShareholders">
                <FileUploadDocumentContainer
                  uploadedFile={{
                    file: {
                      name: listOfShareholders.name,
                      size: listOfShareholders.sizeInBytes,
                    } as File,
                  }}
                  onRemoveDocument={() => setListOfShareholders(null)}
                />
              </EditInvestorCompanyDataForm.Group>
            )}
            {!listOfShareholders && (
              <EditInvestorCompanyDataForm.Group name="listOfShareholders">
                <EditInvestorCompanyDataForm.FileUpload />
              </EditInvestorCompanyDataForm.Group>
            )}
            {transparencyRegister && (
              <EditInvestorCompanyDataForm.Group name="transparencyRegister">
                <FileUploadDocumentContainer
                  uploadedFile={{
                    file: {
                      name: transparencyRegister.name,
                      size: transparencyRegister.sizeInBytes,
                    } as File,
                  }}
                  onRemoveDocument={() => setTransparencyRegister(null)}
                />
              </EditInvestorCompanyDataForm.Group>
            )}
            {!transparencyRegister && (
              <EditInvestorCompanyDataForm.Group name="transparencyRegister">
                <EditInvestorCompanyDataForm.FileUpload />
              </EditInvestorCompanyDataForm.Group>
            )}
          </ModalContent>
          <ModalFooter>
            <ModalFooterButtons
              actionButtons={[
                {
                  name: 'cancel',
                  content: <Translate name="common.cancel" />,
                  size: 'large',
                  onClick: onHideEditCompanyData,
                },
                {
                  name: 'send',
                  content: <Translate name="common.saveChanges" />,
                  variant: 'primary',
                  size: 'large',
                  type: 'submit',
                },
              ]}
            />
          </ModalFooter>
        </EditInvestorCompanyDataForm>
      </Modal>
    </>
  );
};

export default EditCompanyDataModal;
